'use client'

import { cn } from '@/lib/utils'
import * as React from 'react'
import { Label } from './label'
import { Input } from './input'
import { SubtextVisibility, SubtextStrategy, SubtextState } from './subtext'

interface InputFieldProps extends React.ComponentProps<'input'> {
    label?: string
    subtext?: React.ReactNode
    subtextVisibility?: SubtextVisibility
    subtextStrategy?: SubtextStrategy
    reserveSubtextSpace?: boolean
    subtextLive?: 'polite' | 'assertive' | 'off'
    containerClassName?: string
}

const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(({
	className,
	label,
	subtext,
	subtextVisibility = 'hoverOrFocus',
	subtextStrategy,
	reserveSubtextSpace = true,
	subtextLive = 'polite',
	containerClassName,
	disabled,
	onFocus,
	onBlur,
	value,
	...props
}, ref) => {
	const [isHovered, setIsHovered] = React.useState(false)
	const [isFocused, setIsFocused] = React.useState(false)

	const subtextState: SubtextState = {
		focused: isFocused,
		hovered: isHovered,
		disabled: !!disabled,
		value: typeof value === 'string' ? value : undefined,
	}

	const computedSubtext = React.useMemo(() => {
		if (subtextStrategy) {
			return subtextStrategy(subtextState)
		}
		if (subtext) {
			return { text: subtext, visibility: subtextVisibility }
		}
		return { text: undefined, visibility: 'never' as const }
	}, [subtextStrategy, subtextState, subtext, subtextVisibility])

	const isSubtextVisible = React.useMemo(() => {
		if (!computedSubtext.text) return false
		switch (computedSubtext.visibility) {
			case 'always': return true
			case 'hover': return isHovered
			case 'focus': return isFocused
			case 'hoverOrFocus': return isHovered || isFocused
			case 'never': return false
			default: return false
		}
	}, [computedSubtext, isHovered, isFocused])

	const inputElement = (
		<Input 
			ref={ref}
			className={cn(className, disabled && "cursor-not-allowed")} 
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocus={(e) => {
				setIsFocused(true)
				onFocus?.(e)
			}}
			onBlur={(e) => {
				setIsFocused(false)
				onBlur?.(e)
			}}
			disabled={disabled}
			value={value}
			{...props} 
		/>
	)

    const inputContainer = (
        <div 
            className="space-y-0"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {inputElement}
            {reserveSubtextSpace && (
                <div className="mt-0.5 h-[18px] overflow-hidden">
                    <p
                        className={cn(
                            "text-[0.88rem] italic leading-[1.05rem] text-[#3A2E2F]/70 transition-opacity duration-200 truncate",
                            isSubtextVisible ? "opacity-100" : "opacity-0"
                        )}
                        role={computedSubtext.text ? "status" : undefined}
                        aria-live={computedSubtext.text && subtextLive !== 'off' ? subtextLive : undefined}
                        aria-hidden={computedSubtext.text ? undefined : true}
                    >
                        {computedSubtext.text ?? '\u200b'}
                    </p>
                </div>
            )}
        </div>
    )

	if (label) {
		return (
			<div className={cn("space-y-2", containerClassName)}>
				<Label className="text-sm">{label}</Label>
				{inputContainer}
			</div>
		)
	}

	return inputContainer
})

InputField.displayName = 'InputField'

export { InputField }